package com.shadowings.gero.data.lessons.chapter13

import com.shadowings.gero.data.lessons.frenchClause
import com.shadowings.gero.data.lessons.moveGermanSeparableParticleToEnd
import com.shadowings.gero.data.lessons.chapter11.FALLBACK_IO_WORD
import com.shadowings.gero.data.lessons.chapter11.buildWordBankFromWords
import com.shadowings.gero.data.lessons.chapter11.getGerundEn
import com.shadowings.gero.data.lessons.chapter11.getGerundEs
import com.shadowings.gero.data.lessons.chapter11.getGerundIt
import com.shadowings.gero.data.lessons.chapter11.pickPronounOrFallback
import com.shadowings.gero.data.lessons.chapter6.conjugateVerbDe
import com.shadowings.gero.data.lessons.chapter6.conjugateVerbEn
import com.shadowings.gero.data.lessons.chapter6.conjugateVerbEs
import com.shadowings.gero.data.lessons.chapter6.conjugateVerbFr
import com.shadowings.gero.data.lessons.chapter6.conjugateVerbIt
import com.shadowings.gero.data.lessons.formatEnglishObjectPhrase
import com.shadowings.gero.data.lessons.formatEnglishSubject
import com.shadowings.gero.data.lessons.getStativePastParticipleEn
import com.shadowings.gero.data.lessons.isItForcePresent
import com.shadowings.gero.data.lessons.isStativeVerb
import com.shadowings.gero.data.lessons.formatFrenchSubject
import com.shadowings.gero.data.lessons.formatGermanObject
import com.shadowings.gero.data.lessons.formatGermanSubject
import com.shadowings.gero.data.lessons.formatItalianSubject
import com.shadowings.gero.data.lessons.formatSpanishObject
import com.shadowings.gero.data.lessons.animalObjectIsThing
import com.shadowings.gero.data.lessons.isHumanSubject
import com.shadowings.gero.data.lessons.isCommonLivingNoun
import com.shadowings.gero.data.lessons.formatSpanishSubject
import com.shadowings.gero.data.lessons.hasVerbKey
import com.shadowings.gero.data.lessons.pickDistinctIdsByBaseWord
import com.shadowings.gero.data.repository.DatabaseRepositoryImpl
import com.shadowings.gero.data.repository.SentenceOptions
import com.shadowings.gero.domain.model.IQuizContent
import com.shadowings.gero.domain.model.WordSelectionContent
import com.shadowings.gero.presentation.model.Verb
import com.shadowings.gero.presentation.model.Word
import kotlin.random.Random

// Preposition ḏr (M36:D21, transliteration ḏr) introduces a time boundary ("since / from"):
// 1. IERI (sf): "since yesterday" / "da ieri" / "seit gestern" / "desde ayer" / "depuis hier"
// 2. STAMATTINA (dwꜣt): "since this morning" / "da stamattina" / "seit heute Morgen" / "desde esta mañana" / "depuis ce matin"
// 3. ALBA_DEI_TEMPI (pꜣwt): "since the dawn of time" / "dall'alba dei tempi" / "seit der Urzeit" / "desde el albor de los tiempos" / "depuis l'aube des temps"
// 4. EPOCA_DIO (rk + god): "since the age of [god]" / "dall'epoca di [dio]" / "seit der Zeit von [dio]" / "desde la época de [dio]" / "depuis l'époque d'[dio]"
enum class DrTimeComplementType {
    IERI,
    STAMATTINA,
    ALBA_DEI_TEMPI,
    EPOCA_DIO
}


internal val FALLBACK_HORUS_WORD = Word(
    id = "HORUS",
    meaning = "Horus",
    reading = "ḥr",
    gardiner = "G5-A40",
    note = "",
    alt1 = null,
    alt2 = null,
    articolo = "-",
    genitivo = "di",
    articoloEn = "-",
    genitivoEn = "of",
    articoloDe = "-",
    genitivoDe = "von",
    articoloEs = "-",
    genitivoEs = "de",
    isPlural = false,
    showInCompendio = true,
    meaningEn = "Horus",
    meaningDe = "Horus",
    meaningEs = "Horus",
    isFeminine = false,
    articoloFr = "-",
    genitivoFr = "d'",
    meaningFr = "Horus"
)

data class PseudoverbalDrSentence(
    val hieroglyphs: String,
    val transliteration: String,
    val translationIt: String,
    val translationEn: String,
    val translationDe: String,
    val translationEs: String,
    val translationFr: String,
    val isPronominal: Boolean,
    val subjectKey: String,
    val verbKey: String,
    val objectKeys: List<String>,
    val timeComplementType: DrTimeComplementType,
    val godKey: String? = null
) {
    fun getTranslation(languageCode: String): String = when (languageCode) {
        "it" -> translationIt
        "de" -> translationDe
        "es" -> translationEs
        "fr" -> translationFr
        else -> translationEn
    }
}

fun hasDrComplementCandidate(
    verbs: List<Verb>,
    verbSentences: List<SentenceOptions> = DatabaseRepositoryImpl.verbSentences
): Boolean = verbSentences.any { row ->
    row.subjects.isNotEmpty() && verbs.hasVerbKey(row.verbs.first())
}

internal fun pickGodOrFallback(words: List<Word>, random: Random): Word {
    val availableGods = words.filter { it.isDeity }
    return if (availableGods.isNotEmpty()) availableGods.random(random)
    else words.firstOrNull { it.id == "HORUS" } ?: FALLBACK_HORUS_WORD
}

// English: With a duration/starting point marker like "since...", Present Continuous
// ("I am eating since yesterday") is ungrammatical; standard English requires
// the Present Perfect Continuous ("I have been eating since yesterday").
// For stative verbs (which disallow continuous aspect), Present Perfect Simple
// is required ("I have known since yesterday", "he has loved since morning").
private fun pronominalEnglishPrefix(pronoun: Word, verbKey: String, isStative: Boolean = false): String {
    if (isStative) {
        val pp = getStativePastParticipleEn(verbKey)
        return when (pronoun.id) {
            "IO" -> "I have $pp"
            "TU_M" -> "you (m.) have $pp"
            "TU_F" -> "you (f.) have $pp"
            "EGLI" -> "he has $pp"
            "ELLA" -> "she has $pp"
            "ESSO" -> "it has $pp"
            "NOI" -> "we have $pp"
            "VOI" -> "you have $pp"
            "ESSI" -> "they have $pp"
            else -> "he has $pp"
        }
    }
    val g = getGerundEn(verbKey)
    return when (pronoun.id) {
        "IO" -> "I have been $g"
        "TU_M" -> "you (m.) have been $g"
        "TU_F" -> "you (f.) have been $g"
        "EGLI" -> "he has been $g"
        "ELLA" -> "she has been $g"
        "ESSO" -> "it has been $g"
        "NOI" -> "we have been $g"
        "VOI" -> "you have been $g"
        "ESSI" -> "they have been $g"
        else -> "he has been $g"
    }
}

private fun nominalEnglishPrefix(subject: Word, verbKey: String, isStative: Boolean = false): String {
    if (isStative) {
        val pp = getStativePastParticipleEn(verbKey)
        val aux = if (subject.isPlural) "have" else "has"
        return "${formatEnglishSubject(subject)} $aux $pp"
    }
    val g = getGerundEn(verbKey)
    val aux = if (subject.isPlural) "have been" else "has been"
    return "${formatEnglishSubject(subject)} $aux $g"
}

// French: In standard French, "être en train de" is incompatible/redundant with "depuis";
// simple present indicative is required ("je mange depuis ce matin", "l'homme travaille depuis hier").
private fun pronominalFrenchPrefix(pronoun: Word, verbKey: String): String {
    val v = conjugateVerbFr(verbKey, pronoun.id)
    return when (pronoun.id) {
        "IO" -> frenchClause("je", v)
        "TU_M" -> "tu (m.) $v"
        "TU_F" -> "tu (f.) $v"
        "EGLI" -> "il $v"
        "ELLA" -> "elle $v"
        "ESSO" -> "cela $v"
        "NOI" -> "nous $v"
        "VOI" -> "vous $v"
        "ESSI" -> "ils $v"
        else -> "il $v"
    }
}

private fun nominalFrenchPrefix(subject: Word, verbKey: String): String {
    val pronounCode = if (subject.isPlural) "ESSI" else "EGLI"
    val v = conjugateVerbFr(verbKey, pronounCode)
    return "${formatFrenchSubject(subject)} $v"
}

// German: No continuous gerund in German; simple present with "seit" via conjugateVerbDe.
private fun pronominalGermanPrefix(pronoun: Word, verbKey: String, hasObject: Boolean): String {
    val v = conjugateVerbDe(verbKey, pronoun.id, hasObject)
    return when (pronoun.id) {
        "IO" -> "ich $v"
        "TU_M" -> "du (m.) $v"
        "TU_F" -> "du (f.) $v"
        "EGLI" -> "er $v"
        "ELLA" -> "sie $v"
        "ESSO" -> "es $v"
        "NOI" -> "wir $v"
        "VOI" -> "ihr $v"
        "ESSI" -> "sie $v"
        else -> "er $v"
    }
}

private fun nominalGermanPrefix(subject: Word, verbKey: String, hasObject: Boolean): String {
    val pronounCode = if (subject.isPlural) "ESSI" else "EGLI"
    return "${formatGermanSubject(subject)} ${conjugateVerbDe(verbKey, pronounCode, hasObject)}"
}

// Italian: Continuous periphrasis ("sta mangiando ... da") preserves pseudoverbal clarity,
// unless it_force_present is configured for the verb in words.tsv.
private fun pronominalItalianPrefix(pronoun: Word, verbKey: String, itForcePresent: Boolean = false): String {
    if (itForcePresent) {
        val v = conjugateVerbIt(verbKey, pronoun.id)
        return when (pronoun.id) {
            "IO" -> "io $v"
            "TU_M" -> "tu (m.) $v"
            "TU_F" -> "tu (f.) $v"
            "EGLI" -> "egli $v"
            "ELLA" -> "ella $v"
            "ESSO" -> "esso $v"
            "NOI" -> "noi $v"
            "VOI" -> "voi $v"
            "ESSI" -> "essi $v"
            else -> "egli $v"
        }
    }
    val g = getGerundIt(verbKey)
    return when (pronoun.id) {
        "IO" -> "io sto $g"
        "TU_M" -> "tu (m.) stai $g"
        "TU_F" -> "tu (f.) stai $g"
        "EGLI" -> "egli sta $g"
        "ELLA" -> "ella sta $g"
        "ESSO" -> "esso sta $g"
        "NOI" -> "noi stiamo $g"
        "VOI" -> "voi state $g"
        "ESSI" -> "essi stanno $g"
        else -> "egli sta $g"
    }
}

private fun nominalItalianPrefix(subject: Word, verbKey: String, itForcePresent: Boolean = false): String {
    val verbStr = if (itForcePresent) {
        val pronounCode = if (subject.isPlural) "ESSI" else "EGLI"
        conjugateVerbIt(verbKey, pronounCode)
    } else {
        val g = getGerundIt(verbKey)
        val aux = if (subject.isPlural) "stanno" else "sta"
        "$aux $g"
    }
    return "${formatItalianSubject(subject)} $verbStr"
}

// Spanish: Progressive with "desde" ("está comiendo ... desde"),
// or Present Indicative for stative verbs ("conoce ... desde").
private fun pronominalSpanishPrefix(pronoun: Word, verbKey: String, isStative: Boolean = false): String {
    if (isStative) {
        val v = conjugateVerbEs(verbKey, pronoun.id)
        return when (pronoun.id) {
            "IO" -> "yo $v"
            "TU_M" -> "tú (m.) $v"
            "TU_F" -> "tú (f.) $v"
            "EGLI" -> "él $v"
            "ELLA" -> "ella $v"
            "ESSO" -> "eso $v"
            "NOI" -> "nosotros $v"
            "VOI" -> "vosotros $v"
            "ESSI" -> "ellos $v"
            else -> "él $v"
        }
    }
    val g = getGerundEs(verbKey)
    return when (pronoun.id) {
        "IO" -> "yo estoy $g"
        "TU_M" -> "tú (m.) estás $g"
        "TU_F" -> "tú (f.) estás $g"
        "EGLI" -> "él está $g"
        "ELLA" -> "ella está $g"
        "ESSO" -> "eso está $g"
        "NOI" -> "nosotros estamos $g"
        "VOI" -> "vosotros estáis $g"
        "ESSI" -> "ellos están $g"
        else -> "él está $g"
    }
}

private fun nominalSpanishPrefix(subject: Word, verbKey: String, isStative: Boolean = false): String {
    val verbStr = if (isStative) {
        val pronounCode = if (subject.isPlural) "ESSI" else "EGLI"
        conjugateVerbEs(verbKey, pronounCode)
    } else {
        val g = getGerundEs(verbKey)
        val aux = if (subject.isPlural) "están" else "está"
        "$aux $g"
    }
    return "${formatSpanishSubject(subject)} $verbStr"
}

private fun objectPhrase(objectWord: Word?, languageCode: String, verbKey: String = ""): String {
    if (objectWord == null) return ""
    return when (languageCode) {
        "it" -> formatItalianSubject(objectWord)
        "de" -> formatGermanObject(objectWord)
        "es" -> formatSpanishObject(objectWord, animalObjectIsThing(verbKey, DatabaseRepositoryImpl.words))
        "fr" -> formatFrenchSubject(objectWord)
        else -> formatEnglishObjectPhrase(verbKey, formatEnglishSubject(objectWord))
    }
}

internal fun formatDrTimeComplement(
    type: DrTimeComplementType,
    god: Word?,
    languageCode: String
): String = when (type) {
    DrTimeComplementType.IERI -> when (languageCode) {
        "it" -> "da ieri"
        "de" -> "seit gestern"
        "es" -> "desde ayer"
        "fr" -> "depuis hier"
        else -> "since yesterday"
    }
    DrTimeComplementType.STAMATTINA -> when (languageCode) {
        "it" -> "da stamattina"
        "de" -> "seit heute Morgen"
        "es" -> "desde esta mañana"
        "fr" -> "depuis ce matin"
        else -> "since this morning"
    }
    DrTimeComplementType.ALBA_DEI_TEMPI -> when (languageCode) {
        "it" -> "dall'alba dei tempi"
        "de" -> "seit der Urzeit"
        "es" -> "desde el albor de los tiempos"
        "fr" -> "depuis l'aube des temps"
        else -> "since the dawn of time"
    }
    DrTimeComplementType.EPOCA_DIO -> {
        val godWord = god ?: FALLBACK_HORUS_WORD
        when (languageCode) {
            "it" -> "dall'epoca di ${godWord.getMeaning("it")}"
            "de" -> "seit der Zeit von ${godWord.getMeaning("de")}"
            "es" -> "desde la época de ${godWord.getMeaning("es")}"
            "fr" -> {
                val genFr = godWord.getGenitivo("fr")
                val name = godWord.getMeaning("fr")
                if (genFr == "d'") "depuis l'époque d'$name" else "depuis l'époque de $name"
            }
            else -> "since the age of ${godWord.getMeaning("en")}"
        }
    }
}

internal fun drTimeComplementHiero(
    type: DrTimeComplementType,
    god: Word?,
    epocaWord: Word? = null,
    ieriWord: Word? = null,
    stamattinaWord: Word? = null,
    albaWord: Word? = null
): String = when (type) {
    DrTimeComplementType.IERI -> ieriWord?.gardiner ?: "S29-I9:N5"
    DrTimeComplementType.STAMATTINA -> stamattinaWord?.gardiner ?: "X1:N14-G1-N5"
    DrTimeComplementType.ALBA_DEI_TEMPI -> albaWord?.gardiner ?: "Q3w0.5-G40-X1:N9:Z2"
    DrTimeComplementType.EPOCA_DIO -> "${epocaWord?.gardiner ?: "D21:V31-N5"}-${(god ?: FALLBACK_HORUS_WORD).gardiner}"
}

internal fun drTimeComplementTranslit(
    type: DrTimeComplementType,
    god: Word?,
    epocaWord: Word? = null,
    ieriWord: Word? = null,
    stamattinaWord: Word? = null,
    albaWord: Word? = null
): String = when (type) {
    DrTimeComplementType.IERI -> ieriWord?.reading ?: "sf"
    DrTimeComplementType.STAMATTINA -> stamattinaWord?.reading ?: "dwꜣt"
    DrTimeComplementType.ALBA_DEI_TEMPI -> albaWord?.reading ?: "pꜣwt"
    DrTimeComplementType.EPOCA_DIO -> "${epocaWord?.reading ?: "rk"} ${(god ?: FALLBACK_HORUS_WORD).reading}"
}

fun getRandomDrComplementSentence(
    verbs: List<Verb>,
    words: List<Word>,
    isPronominal: Boolean? = null,
    timeType: DrTimeComplementType? = null,
    godWord: Word? = null,
    random: Random = Random,
    verbSentences: List<SentenceOptions> = DatabaseRepositoryImpl.verbSentences
): PseudoverbalDrSentence {
    val candidates = verbSentences.filter { row ->
        row.subjects.isNotEmpty() && verbs.hasVerbKey(row.verbs.first())
    }
    require(candidates.isNotEmpty()) { "Nessuna riga verbale disponibile per frasi con ḏr" }

    val pronominal = isPronominal ?: random.nextBoolean()
    val humanCandidates = candidates.filter { row -> row.subjects.any { isHumanSubject(it, words) } }
    val chosenRow = if (pronominal && humanCandidates.isNotEmpty()) {
        humanCandidates.random(random)
    } else {
        candidates.random(random)
    }

    val verb = verbs.first { it.chiaveWords == chosenRow.verbs.first() }
    val usedBaseIds = mutableSetOf<String>()

    val objectWord = pickDistinctIdsByBaseWord(chosenRow.objects.shuffled(random), 1, words, usedBaseIds)
        .firstOrNull()
        ?.let { id -> words.firstOrNull { it.id == id } }

    val selectedType = timeType ?: DrTimeComplementType.entries.random(random)
    val selectedGod = if (selectedType == DrTimeComplementType.EPOCA_DIO) {
        godWord ?: pickGodOrFallback(words, random)
    } else null

    val infHiero = verb.formaInfinita.ifBlank { verb.formaBaseSenzaDet }
    val infTrans = verb.traslitterazioneInfinita.ifBlank { verb.traslitterazione }
    val objectHieroPart = objectWord?.let { "-${it.gardiner}" } ?: ""
    val objectTransPart = objectWord?.let { " ${it.reading}" } ?: ""

    val epocaWord = words.firstOrNull { it.id == "EPOCA" }
    val ieriWord = words.firstOrNull { it.id == "IERI" }
    val stamattinaWord = words.firstOrNull { it.id == "STAMATTINA" }
    val albaWord = words.firstOrNull { it.id == "ALBA_DEI_TEMPI" }

    val timeHiero = drTimeComplementHiero(selectedType, selectedGod, epocaWord, ieriWord, stamattinaWord, albaWord)
    val timeTrans = drTimeComplementTranslit(selectedType, selectedGod, epocaWord, ieriWord, stamattinaWord, albaWord)

    val subjectWord: Word
    val hieroglyphs: String
    val transliteration: String

    if (pronominal) {
        val pronoun = pickPronounOrFallback(words, random).let { candidate ->
            val availableInRow = chosenRow.subjects
                .mapNotNull { id -> words.firstOrNull { it.id == id && it.isPronoun } }
            availableInRow.randomOrNull(random) ?: candidate
        }
        subjectWord = pronoun
        val pronTrans = if (pronoun.reading.startsWith(".")) pronoun.reading else ".${pronoun.reading}"
        hieroglyphs =
            "M17-G43-${pronoun.gardiner}-D2h2:Z1-$infHiero$objectHieroPart-M36:D21-$timeHiero"
        transliteration = "iw$pronTrans ḥr $infTrans$objectTransPart ḏr $timeTrans"
    } else {
        val availableNounsInRow = pickDistinctIdsByBaseWord(chosenRow.subjects.shuffled(random), 1, words, mutableSetOf())
            .mapNotNull { id -> words.firstOrNull { it.id == id && !it.isPronoun } }
        subjectWord = availableNounsInRow.firstOrNull()
            ?: words.firstOrNull { it.isCommonLivingNoun() }
            ?: FALLBACK_IO_WORD
        hieroglyphs =
            "M17-G43-${subjectWord.gardiner}-D2h2:Z1-$infHiero$objectHieroPart-M36:D21-$timeHiero"
        transliteration = "iw ${subjectWord.reading} ḥr $infTrans$objectTransPart ḏr $timeTrans"
    }

    fun translationFor(languageCode: String): String {
        val isStative = isStativeVerb(verb.chiaveWords, words)
        val itForcePresent = isItForcePresent(verb.chiaveWords, words)
        val prefix = when (languageCode) {
            "en" -> if (pronominal) pronominalEnglishPrefix(subjectWord, verb.chiaveWords, isStative) else nominalEnglishPrefix(subjectWord, verb.chiaveWords, isStative)
            "fr" -> if (pronominal) pronominalFrenchPrefix(subjectWord, verb.chiaveWords) else nominalFrenchPrefix(subjectWord, verb.chiaveWords)
            "de" -> if (pronominal) pronominalGermanPrefix(subjectWord, verb.chiaveWords, objectWord != null) else nominalGermanPrefix(subjectWord, verb.chiaveWords, objectWord != null)
            "es" -> if (pronominal) pronominalSpanishPrefix(subjectWord, verb.chiaveWords, isStative) else nominalSpanishPrefix(subjectWord, verb.chiaveWords, isStative)
            else -> if (pronominal) pronominalItalianPrefix(subjectWord, verb.chiaveWords, itForcePresent) else nominalItalianPrefix(subjectWord, verb.chiaveWords, itForcePresent)
        }
        val objStr = objectPhrase(objectWord, languageCode, verb.chiaveWords)
        val compStr = formatDrTimeComplement(selectedType, selectedGod, languageCode)
        return listOf(prefix, objStr, compStr).filter { it.isNotBlank() }.joinToString(" ")
            .let { if (languageCode == "de") moveGermanSeparableParticleToEnd(it) else it }
    }

    return PseudoverbalDrSentence(
        hieroglyphs = hieroglyphs,
        transliteration = transliteration,
        translationIt = translationFor("it"),
        translationEn = translationFor("en"),
        translationDe = translationFor("de"),
        translationEs = translationFor("es"),
        translationFr = translationFor("fr"),
        isPronominal = pronominal,
        subjectKey = subjectWord.id,
        verbKey = verb.chiaveWords,
        objectKeys = objectWord?.let { listOf(it.id) } ?: emptyList(),
        timeComplementType = selectedType,
        godKey = selectedGod?.id
    )
}

fun buildDrComplementWordSelectionQuizzes(
    count: Int,
    random: Random,
    languageCode: String,
    verbSentences: List<SentenceOptions> = DatabaseRepositoryImpl.verbSentences,
    words: List<Word> = DatabaseRepositoryImpl.words,
    verbs: List<Verb> = DatabaseRepositoryImpl.verbs
): List<IQuizContent> {
    if (!hasDrComplementCandidate(verbs, verbSentences)) return emptyList()
    val quizzes = mutableListOf<IQuizContent>()
    for (i in 0 until count) {
        var attempts = 0
        var quiz: WordSelectionContent? = null
        while (attempts < 10) {
            val sentence = getRandomDrComplementSentence(
                verbs = verbs,
                words = words,
                random = random,
                verbSentences = verbSentences
            )
            val correctPhrase = sentence.getTranslation(languageCode)
            val isDuplicate = quizzes.any { (it as? WordSelectionContent)?.correctPhrase == correctPhrase }
            val wordBank = buildWordBankFromWords(correctPhrase, words, verbs, languageCode, random)

            quiz = WordSelectionContent(
                hieroglyphs = sentence.hieroglyphs,
                correctPhrase = correctPhrase,
                words = wordBank,
                transliteration = null
            )
            if (!isDuplicate) break
            attempts++
        }
        quiz?.let { quizzes.add(it) }
    }
    return quizzes
}
